<template>
  <div class="sliding-scrollable-container" ref="container">
    <!-- ===== HEADER ===== -->
    <!-- Desktop/Tablet header -->
    <div class="section-header" v-if="display.mdAndUp.value">
      <h2 class="section-title" :class="titleClasses">{{ title }}</h2>
      <router-link v-if="seeMoreLink" :to="seeMoreLink" class="see-more-link">
        {{ seeMoreText }} <v-icon small>mdi-arrow-right</v-icon>
      </router-link>
    </div>

    <!-- Mobile header -->
    <div class="mobile-section-header" v-else>
      <div class="mobile-section-title">{{ title }}</div>
      <router-link
        v-if="seeMoreLink"
        :to="seeMoreLink"
        class="mobile-circle-next"
      >
        <v-icon size="18">mdi-chevron-right</v-icon>
      </router-link>
    </div>

    <!-- ===== STATES ===== -->
    <div v-if="loading" class="loading-container">
      <div class="listing-cards-skeleton">
        <div
          class="listing-card-skeleton"
          v-for="n in 5"
          :key="`skeleton-${n}`"
        >
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-price"></div>
            <div class="skeleton-details"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="error-container">
      <v-icon color="error" large>mdi-alert-circle</v-icon>
      <p>{{ error }}</p>
      <v-btn color="primary" @click="$emit('retry')" small>Retry</v-btn>
    </div>

    <div
      v-else-if="!displayListings || displayListings.length === 0"
      class="empty-container"
    >
      <v-icon color="grey" large>mdi-home-city-outline</v-icon>
      <p>{{ emptyMessage }}</p>
      <router-link v-if="emptyActionLink" :to="emptyActionLink">
        <v-btn color="primary" small>{{ emptyActionText }}</v-btn>
      </router-link>
    </div>

    <!-- ===== DESKTOP/TABLET: your original horizontal slider (UNCHANGED) ===== -->
    <div v-else-if="display.mdAndUp.value" class="listing-slider-container">
      <button
        class="nav-button prev-button"
        @click="scrollLeft"
        :disabled="!canScrollLeft"
        v-show="isScrollable"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </button>

      <div class="listing-slider" ref="slider" @scroll="handleScroll">
        <div
          v-for="listing in displayListings"
          :key="listing.id"
          class="listing-card"
          :class="{ 'job-listing-card': isJobListing(listing) }"
          @click="viewListingDetails(listing)"
        >
          <!-- Favorite -->
          <button
          v-if="user?.role?.slug !== 'admin'"
            class="favorite-button"
            @click.stop="handleToggleFavorite(listing)"
            :class="{ favorited: isFavorited(listing.id) }"
          >
            <v-icon
              :color="isFavorited(listing.id) ? '#e50000' : '#666'"
              size="18"
            >
              {{ isFavorited(listing.id) ? "mdi-heart" : "mdi-heart-outline" }}
            </v-icon>
          </button>

          <!-- Image -->
          <div v-if="!isJobListing(listing)" class="listing-image-container">
            <img
              :src="getListingImage(listing)"
              :alt="listing.title"
              class="listing-image"
              loading="lazy"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div class="featured-badge" v-if="listing.isFeatured">
              <v-icon size="12">mdi-star</v-icon>
              <span>Featured</span>
            </div>
            <div class="verified-badge" v-if="listing.verified">
              <v-icon size="12">mdi-check-decagram</v-icon>
              <span>Verified</span>
            </div>
          </div>

          <!-- Content -->
          <div
            class="listing-content"
            :class="{ 'job-content': isJobListing(listing) }"
          >
            <div v-if="isJobListing(listing)" class="job-badges">
              <span v-if="listing.isFeatured" class="feature-badge"
                >FEATURED</span
              >
              <span v-if="listing.verified" class="verified-badge">
                <v-icon size="12" color="white" class="me-1"
                  >mdi-check-circle</v-icon
                >
                VERIFIED USER
              </span>
            </div>

            <div class="title-category-row">
              <h3
                class="listing-title"
                :class="{ 'job-title': isJobListing(listing) }"
              >
                {{ listing.title }}
              </h3>

              <div class="category-name" v-if="!isJobListing(listing)">
                {{ listing.category?.name || "-" }}
              </div>
              <div class="company-name" v-else>
                {{
                  listing.company_name ||
                  listing.job?.company_name ||
                  "Confidential"
                }}
              </div>
            </div>

            <div class="listing-price">
              <span class="currency">{{ listing.currency || "AED" }}</span>

              {{ " " }}
              <span class="amount">
                {{
                  isJobListing(listing)
                    ? listing?.job_details?.salaryExpectations
                    : listing.price
                    ? formatPrice(listing.price)
                    : "Negotiable"
                }}
              </span>
            </div>

            <template v-if="!isJobListing(listing)">
              <template v-if="isPropertyListing(listing)">
                <div class="listing-specs property-first">
                  <div v-if="listing.property_details" class="property-specs">
                    <div
                      v-if="listing.property_details.bedrooms !== undefined"
                      class="spec-item"
                    >
                      <v-icon small>mdi-bed</v-icon>
                      <span
                        >{{ listing.property_details.bedrooms }}
                        {{
                          listing.property_details.bedrooms === 1
                            ? "bed"
                            : "beds"
                        }}</span
                      >
                    </div>
                    <div
                      v-if="listing.property_details.bathrooms !== undefined"
                      class="spec-item"
                    >
                      <v-icon small>mdi-shower</v-icon>
                      <span
                        >{{ listing.property_details.bathrooms }}
                        {{
                          listing.property_details.bathrooms === 1
                            ? "bath"
                            : "baths"
                        }}</span
                      >
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="isCarListing(listing)">
                <div class="listing-specs">
                  <div v-if="listing.car_details" class="car-specs">
                    <div v-if="listing.car_details.year" class="spec-item">
                      <v-icon small>mdi-calendar</v-icon>
                      <span>{{ listing.car_details.year }}</span>
                    </div>
                    <div
                      v-if="listing.car_details.kilometers"
                      class="spec-item"
                    >
                      <v-icon small>mdi-speedometer</v-icon>
                      <span
                        >{{
                          formatNumber(listing.car_details.kilometers)
                        }}
                        km</span
                      >
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <div class="listing-location">
              <v-icon small color="grey darken-2">mdi-map-marker</v-icon>
              {{ getLocationDisplay(listing) }}
            </div>
          </div>
        </div>
      </div>

      <button
        class="nav-button next-button"
        @click="scrollRight"
        :disabled="!canScrollRight"
        v-show="isScrollable"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- ===== MOBILE: 2-column grid with "View All" as last tile ===== -->
    <div v-else class="mobile-grid-wrap">
      <div class="mobile-grid">
        <div
          v-for="listing in mobileGridItems"
          :key="listing.id"
          class="mobile-card"
          :class="{ 'mobile-viewall': isViewAll(listing) }"
          @click="
            isViewAll(listing) ? goSeeMore() : viewListingDetails(listing)
          "
        >
          <!-- View All tile -->
          <template v-if="isViewAll(listing)">
            <router-link
              :to="resolvedSeeMore"
              class="mobile-viewall-wrap router-link"
              @click.stop
            >
              <div class="mobile-viewall-circle">
                <v-icon>mdi-chevron-right</v-icon>
              </div>
              <div class="mobile-viewall-text">View All</div>
            </router-link>
          </template>

          <!-- Normal listing tile -->
          <template v-else>
            <div v-if="!isJobListing(listing)" class="mobile-img">
              <img
                :src="getListingImage(listing)"
                :alt="listing.title"
                loading="lazy"
              />
            </div>

            <h3
              class="listing-title"
              :class="{ 'job-title': isJobListing(listing) }"
            >
              {{ listing.title }}
            </h3>

            <div class="mobile-price">
              {{
                (listing.currency || "AED") +
                " " +
                (isJobListing(listing)
                  ? listing?.job_details?.salaryExpectations || "Negotiable"
                  : formatPrice(listing.price))
              }}
            </div>
            <div class="mobile-meta">
              {{ getMobileMeta(listing) }}
            </div>
            <div class="mobile-loc">
              {{ getLocationDisplay(listing) }}
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Dots (desktop slider only) -->
    <!-- <div
      class="scroll-indicators"
      v-if="!display.mobile.value && isScrollable && displayListings.length > 4"
    >
      <div
        v-for="(_, index) in Math.min(scrollPositions.length, 8)"
        :key="index"
        class="scroll-indicator"
        :class="{ active: currentScrollIndex === index }"
        @click="scrollToIndex(index)"
      ></div>
    </div> -->
  </div>
</template>

<script setup>
import { useFont } from "@/composables/useFont"; // Import the font composable
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

// Use the font composable
const { fontFamilyClass, fontSizeClass, fontWeightClass } = useFont();

// ===== PROPS =====
const props = defineProps({
  title: { type: String, required: true },
  titleClasses: { type: String, default: "" },
  listings: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  favorites: { type: Array, default: () => [] },
  seeMoreLink: { type: [String, Object], default: null },
  seeMoreText: { type: String, default: "See more listings" },
  emptyMessage: {
    type: String,
    default: "No listings available at the moment",
  },
  emptyActionLink: { type: [String, Object], default: null },
  emptyActionText: { type: String, default: "Browse all listings" },
  useTestData: { type: Boolean, default: false },
  sectionType: { type: String, default: "general" },
  // Add font system props
  fontFamily: {
    type: String,
    default: "primary", // primary or secondary
  },
  fontSize: {
    type: String,
    default: "base", // xs, sm, base, lg, xl, 2xl, 3xl, 4xl
  },
  fontWeight: {
    type: String,
    default: "regular", // light, regular, medium, semibold, bold
  },
});

// Computed font classes
const computedFontClasses = computed(() => {
  return {
    [`font-${props.fontFamily}`]: true,
    [`text-${props.fontSize}`]: true,
    [`font-${props.fontWeight}`]: true,
  };
});

// ===== EMITS =====
const emit = defineEmits(["viewDetails", "retry", "toggleFavorite"]);

// ===== SETUP =====
const router = useRouter();
const store = useStore();
const slider = ref(null);
const container = ref(null);

// ===== SCROLL STATE =====
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const isScrollable = ref(false);
const currentScrollIndex = ref(0);

import { useDisplay } from "vuetify";
const display = useDisplay();
const user = computed(() => store.state.auth.user);

// ===== DEVELOPMENT LOGGING =====
const devLog = (message, ...args) => {
  if (process.env.NODE_ENV === "development") {
    console.log(message, ...args);
  }
};

const devError = (message, ...args) => {
  if (process.env.NODE_ENV === "development") {
    console.error(message, ...args);
  } else {
    console.error(message);
  }
};

// ✅ NEW: Working favorites logic - copied exactly from your working component
const isFavorited = (listingId) => {
  return store.getters["favorite/isFavorited"](listingId);
};

// ✅ NEW: Pure event handler - emit to parent instead of handling store directly
const handleToggleFavorite = (listing) => {
  console.log(
    "💖 SlidingScrollable: Emitting favorite toggle for:",
    listing.id
  );

  // Emit the event with the listing data
  emit("toggleFavorite", listing);
};

// ===== ASSET IMAGES =====
const assetImages = [
  require("@/assets/pexels-alex-staudinger-829197-1732414.jpg"),
  require("@/assets/pexels-binyaminmellish-186077.jpg"),
  require("@/assets/pexels-eye4dtail-129494.jpg"),
  require("@/assets/pexels-foteros-783745.jpg"),
  require("@/assets/pexels-jvdm-1457842.jpg"),
  require("@/assets/pexels-sevenstormphotography-439391.jpg"),
  require("@/assets/pexels-sevenstormphotography-981916.jpg"),
];

// ===== ENHANCED MOCK DATA GENERATOR =====
const generatePlaceholderListings = (count = 4) => {
  const categories = [
    { id: 1, name: "Motors" },
    { id: 2, name: "Jobs" },
    { id: 3, name: "Property for Sale" },
    { id: 4, name: "Property for Rent" },
    { id: 5, name: "Community" },
    { id: 6, name: "Classifieds" },
    { id: 7, name: "Furniture & Garden" },
    { id: 8, name: "Mobiles & Tablets" },
  ];

  const cities = [
    "Mogadishu",
    "Galkayo",
    "Hargeisa",
    "Bosaso",
    "Kismayo",
    "Baidoa",
  ];
  const makeModels = [
    "Toyota Camry",
    "Honda Civic",
    "BMW X5",
    "Mercedes-Benz C-Class",
    "Audi A4",
    "Nissan Altima",
  ];
  const bodyTypes = [
    "Sedan",
    "SUV",
    "Hatchback",
    "Coupe",
    "Wagon",
    "Convertible",
  ];

  const titles = {
    Motors: [
      "Luxury SUV for Sale",
      "Motorcycle Honda",
      "Car Parts Available",
      "Truck for Rent",
    ],
    "Property for Sale": [
      "Modern Villa",
      "Apartment Downtown",
      "Commercial Building",
      "Land Plot",
    ],
    "Property for Rent": [
      "Furnished Apartment",
      "Office Space",
      "Studio Flat",
      "Family House",
    ],
    Jobs: [
      "Software Developer",
      "Sales Manager",
      "Teacher Position",
      "Driver Needed",
    ],
    Classifieds: [
      "Electronics Bundle",
      "Furniture Set",
      "Books Collection",
      "Tools Kit",
    ],
    Community: [
      "Tutoring Services",
      "Event Planning",
      "Fitness Classes",
      "Language Exchange",
    ],
    "Furniture & Garden": [
      "Dining Table",
      "Garden Tools",
      "Sofa Set",
      "Outdoor Furniture",
    ],
    "Mobiles & Tablets": [
      "iPhone 14",
      "Samsung Galaxy",
      "iPad Pro",
      "Android Tablet",
    ],
  };

  return Array.from({ length: count }, (_, index) => {
    const category = categories[index % categories.length];
    const categoryTitles = titles[category.name] || ["Sample Item"];
    const title = categoryTitles[index % categoryTitles.length];
    const isProperty = category.name.includes("Property");
    const isCar = category.name === "Motors";
    const isJob = category.name === "Jobs";

    const listing = {
      id: `placeholder-${index + 1}`,
      title,
      description: `Quality ${title.toLowerCase()} available in ${
        cities[index % cities.length]
      }`,
      price: Math.floor(Math.random() * 100000) + 1000,
      currency: "USD",
      isFeatured: index % 3 === 0,
      verified: index % 2 === 0,
      city_name: cities[index % cities.length],
      category,
      category_id: category.id,
      sub_category: isProperty ? { id: index + 10, name: "Residential" } : null,
      image_urls: [assetImages[index % assetImages.length]],
      images: [assetImages[index % assetImages.length]],
      status: 2,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      location_data: {
        city_name: cities[index % cities.length],
        city: cities[index % cities.length],
        country: "Somalia",
        country_code: "SO",
      },
    };

    // ✅ ENHANCED: Add both new and legacy property structures
    if (isProperty) {
      // New enhanced structure
      listing.property_details = {
        bedrooms: Math.floor(Math.random() * 5) + 1,
        bathrooms: Math.floor(Math.random() * 3) + 1,
        size: Math.floor(Math.random() * 2000) + 500,
        propertyType: ["Apartment", "Villa", "Studio", "Townhouse"][index % 4],
        isFurnished: index % 2 === 0,
      };

      // Legacy structure for backward compatibility
      listing.property = {
        bedrooms: listing.property_details.bedrooms,
        bathrooms: listing.property_details.bathrooms,
        size: listing.property_details.size,
        propertyType: listing.property_details.propertyType,
        isFurnished: listing.property_details.isFurnished,
      };
    }

    // ✅ ENHANCED: Add car details for motor listings
    if (isCar) {
      listing.car_details = {
        makeModel: makeModels[index % makeModels.length],
        year: 2015 + Math.floor(Math.random() * 10),
        bodyType: bodyTypes[index % bodyTypes.length],
        kilometers: Math.floor(Math.random() * 200000) + 10000,
        fuel_type: ["Petrol", "Diesel", "Hybrid", "Electric"][index % 4],
        transmission_type: ["Manual", "Automatic"][index % 2],
      };
    }

    // ✅ ENHANCED: Add job details for job listings
    if (isJob) {
      listing.job = {
        employment_type: [
          "Full Time",
          "Part Time",
          "Contract",
          "Temporary",
          "Internship",
        ][index % 5],
        experience: index % 2 === 0 ? 0 : Math.floor(Math.random() * 10) + 1,
        education: [
          "N/A",
          "High School",
          "Bachelor's Degree",
          "Master's Degree",
          "PhD",
        ][index % 5],
        salary_min: index % 2 === 0 ? 500 : null,
        salary_max: index % 2 === 0 ? 1500 : null,
        has_commission: index % 2 === 0,
        company_name: `Company ${index + 1}`,
      };
      listing.salary_min = listing.job.salary_min;
      listing.salary_max = listing.job.salary_max;
      listing.company_name = listing.job.company_name;
    }

    return listing;
  });
};

// ===== COMPUTED PROPERTIES =====
const displayListings = computed(() => {
  if (props.useTestData || !props.listings || props.listings.length === 0) {
    return generatePlaceholderListings();
  }
  return props.listings;
});

const scrollPositions = computed(() => {
  if (
    !slider.value ||
    !displayListings.value?.length ||
    displayListings.value.length <= 4
  ) {
    return [];
  }

  const cardWidth = 296; // 280px + 16px gap
  const visibleWidth = slider.value.clientWidth;
  const totalWidth = displayListings.value.length * cardWidth;

  if (totalWidth <= visibleWidth) {
    return [];
  }

  const scrollableWidth = totalWidth - visibleWidth;
  const positions = Math.ceil(scrollableWidth / (visibleWidth * 0.8)) + 1;

  return Array.from({ length: Math.min(positions, 8) }, (_, i) => i);
});

// ===== ENHANCED CATEGORY DETECTION METHODS =====

/**
 * ✅ ENHANCED: Detect if listing is a car/motor listing
 */
const isCarListing = (listing) => {
  return (
    listing.car_details ||
    listing.category?.name?.toLowerCase().includes("motor") ||
    listing.category_id === 1
  );
};

/**
 * ✅ ENHANCED: Detect if listing is a property listing
 */
const isPropertyListing = (listing) => {
  return (
    listing.property_details ||
    listing.property ||
    listing.category?.name?.toLowerCase().includes("property") ||
    [3, 4].includes(listing.category_id)
  );
};

/**
 * ✅ ADD: Job listing detection function (consistent with other components)
 */
const isJobListing = (listing) => {
  // Check if category ID is for jobs (usually 2) or subcategory ID is for jobs (16)
  return (
    listing.category_id === 2 ||
    listing.category?.id === 2 ||
    listing.sub_category?.id === 16 ||
    listing.subcategory?.id === 16 ||
    (listing.category?.name &&
      listing.category.name.toLowerCase().includes("job"))
  );
};

// ✅ ADD: Job specific helper methods
function formatSalary(listing) {
  const min =
    listing.salary_min || listing.job?.salary_min || listing.min_salary;
  const max =
    listing.salary_max || listing.job?.salary_max || listing.max_salary;

  if (min && max) {
    return `${formatPrice(min)} - ${formatPrice(max)} per month${
      listing.has_commission ? " + Commission" : ""
    }`;
  } else if (min) {
    return `From ${formatPrice(min)} per month`;
  } else if (max) {
    return `Up to ${formatPrice(max)} per month`;
  } else {
    return "Salary negotiable";
  }
}

function getEmploymentType(listing) {
  const type = listing.employment_type || listing.job?.employment_type;
  if (!type) return "Full Time";

  const types = {
    full_time: "Full Time",
    part_time: "Part Time",
    contract: "Contract",
    temporary: "Temporary",
    internship: "Internship",
  };

  return types[type.toLowerCase()] || type;
}

function getExperience(listing) {
  const exp =
    listing.experience || listing.job?.experience || listing.min_experience;
  if (!exp) return "0-1 Years";

  if (typeof exp === "number") {
    return exp === 0 ? "Entry Level" : `${exp}+ Years`;
  }

  return exp;
}

function getEducation(listing) {
  const edu =
    listing.education || listing.job?.education || listing.min_education;
  if (!edu) return "N/A";
  return edu;
}

// ===== ENHANCED DISPLAY METHODS =====

/**
 * ✅ ENHANCED: Location display with city and country
 */
const getLocationDisplay = (listing) => {
  // Priority: location_data > city_name > fallback
  if (listing.location_data) {
    const parts = [];
    if (listing.location_data.city || listing.location_data.city_name) {
      parts.push(listing.location_data.city || listing.location_data.city_name);
    }
    if (listing.location_data.country) {
      parts.push(listing.location_data.country);
    }
    if (parts.length > 0) {
      return parts.join(", ");
    }
  }

  return listing.city_name || "Location not specified";
};

/**
 * ✅ ENHANCED: Property type label mapping
 */
const getPropertyTypeLabel = (propertyType) => {
  const typeMap = {
    1: "Rent",
    2: "Sale",
    apartment: "Apartment",
    villa: "Villa",
    studio: "Studio",
    townhouse: "Townhouse",
    commercial: "Commercial",
    land: "Land",
  };

  return typeMap[propertyType] || propertyType || "Property";
};

/**
 * ✅ ENHANCED: Number formatting utility
 */
const formatNumber = (number) => {
  if (!number) return "";
  return parseInt(number).toLocaleString();
};

/**
 * ✅ ENHANCED: Price formatting (already bold and red in CSS)
 */
const formatPrice = (price) => {
  if (!price) return "Price on request";
  const numPrice = typeof price === "string" ? parseFloat(price) : price;
  return numPrice.toLocaleString();
};

// ===== UTILITY METHODS =====

/**
 * ✅ ENHANCED: Better listing type detection
 */
const detectListingType = (listing) => {
  if (!listing) return "general";

  // Check if it has property-specific data
  if (listing.property || listing.property_details) return "property";

  // Check category ID (property categories)
  const categoryId = listing.category_id || listing.category?.id;
  if (categoryId === 3 || categoryId === 4) return "property";

  // Check category name
  const categoryName = listing.category?.name?.toLowerCase() || "";
  if (categoryName.includes("property")) return "property";

  return "general";
};

// ===== SCROLL METHODS =====
const updateScrollState = () => {
  if (!slider.value || !displayListings.value?.length) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    isScrollable.value = false;
    return;
  }

  const { scrollLeft, scrollWidth, clientWidth } = slider.value;
  const hasOverflow = scrollWidth > clientWidth;

  isScrollable.value = hasOverflow;
  canScrollLeft.value = hasOverflow && scrollLeft > 10;
  canScrollRight.value =
    hasOverflow && scrollLeft < scrollWidth - clientWidth - 10;

  // Update scroll index
  if (scrollPositions.value.length > 0) {
    const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
    currentScrollIndex.value = Math.round(
      scrollPercentage * (scrollPositions.value.length - 1)
    );
  }
};

let scrollThrottleTimeout = null;
const handleScroll = () => {
  if (scrollThrottleTimeout) return;

  scrollThrottleTimeout = setTimeout(() => {
    updateScrollState();
    scrollThrottleTimeout = null;
  }, 16); // ~60fps
};

const scrollLeft = () => {
  if (!slider.value || !canScrollLeft.value) return;

  const scrollAmount = Math.min(slider.value.clientWidth * 0.8, 640);
  devLog(`⬅️ Scrolling left by ${scrollAmount}px`);

  slider.value.scrollBy({
    left: -scrollAmount,
    behavior: "smooth",
  });
};

const scrollRight = () => {
  if (!slider.value || !canScrollRight.value) return;

  const scrollAmount = Math.min(slider.value.clientWidth * 0.8, 640);
  devLog(`➡️ Scrolling right by ${scrollAmount}px`);

  slider.value.scrollBy({
    left: scrollAmount,
    behavior: "smooth",
  });
};

const scrollToIndex = (index) => {
  if (!slider.value || !scrollPositions.value.length) return;

  const cardWidth = 296;
  const visibleWidth = slider.value.clientWidth;
  const scrollableWidth =
    displayListings.value.length * cardWidth - visibleWidth;
  const scrollPerIndex = scrollableWidth / (scrollPositions.value.length - 1);

  const targetPosition = index * scrollPerIndex;
  slider.value.scrollTo({ left: targetPosition, behavior: "smooth" });
  currentScrollIndex.value = index;
};

// ===== IMAGE HANDLING METHODS =====
const normalizeListing = (listing) => {
  if (!listing) return null;

  const baseUrl = process.env.VUE_APP_BASE_URL || "";

  // Normalize image URLs
  let normalizedImages = [];

  // Handle image_urls
  if (listing.image_urls && Array.isArray(listing.image_urls)) {
    normalizedImages = listing.image_urls
      .map((url) => {
        if (typeof url === "string" && url.trim() && url !== "[") {
          if (url.startsWith("/storage/") || url.startsWith("/")) {
            return `${baseUrl}${url}`;
          }
          if (url.startsWith("http")) {
            return url;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  // Handle images array
  if (
    !normalizedImages.length &&
    listing.images &&
    Array.isArray(listing.images)
  ) {
    normalizedImages = listing.images
      .map((img) => {
        if (typeof img === "string" && img.trim() && img !== "[") {
          if (img.startsWith("/storage/") || img.startsWith("/")) {
            return `${baseUrl}${img}`;
          }
          if (img.startsWith("http")) {
            return img;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  // Handle files array
  if (
    !normalizedImages.length &&
    listing.files &&
    Array.isArray(listing.files)
  ) {
    normalizedImages = listing.files
      .map((file) => {
        const url = file.url || file.file_path;
        if (url && typeof url === "string" && url.trim() && url !== "[") {
          if (url.startsWith("/storage/") || url.startsWith("/")) {
            return `${baseUrl}${url}`;
          }
          if (url.startsWith("http")) {
            return url;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  return {
    ...listing,
    image_urls: normalizedImages,
    images: normalizedImages,
    // Ensure other essential fields
    price: parseFloat(listing.price) || 0,
    isFeatured:
      listing.isFeatured === "1" ||
      listing.isFeatured === 1 ||
      listing.isFeatured === true,
    verified: !!listing.verified,
    category_id: listing.category_id || listing.category?.id,
    sub_category_id: listing.sub_category_id || listing.subcategory?.id,
  };
};

const getListingImage = (listing) => {
  try {
    // Placeholder images
    if (listing.id && listing.id.toString().startsWith("placeholder-")) {
      return listing.image_urls?.[0] || listing.images?.[0] || assetImages[0];
    }

    // Handle image_urls with better type checking
    if (listing.image_urls) {
      let imageUrls = [];

      // Handle array case
      if (Array.isArray(listing.image_urls)) {
        imageUrls = listing.image_urls;
      }
      // Handle string case (single URL)
      else if (typeof listing.image_urls === "string") {
        imageUrls = [listing.image_urls];
      }

      // Find first valid URL
      for (const imageUrl of imageUrls) {
        if (
          imageUrl &&
          typeof imageUrl === "string" &&
          imageUrl.trim() !== "" &&
          imageUrl !== "["
        ) {
          if (imageUrl.startsWith("/storage/") || imageUrl.startsWith("/")) {
            const baseUrl = process.env.VUE_APP_BASE_URL || "";
            return `${baseUrl}${imageUrl}`;
          }

          if (
            imageUrl.startsWith("http://") ||
            imageUrl.startsWith("https://")
          ) {
            return imageUrl;
          }
        }
      }
    }

    // Handle files array with better validation
    if (
      listing.files &&
      Array.isArray(listing.files) &&
      listing.files.length > 0
    ) {
      const file = listing.files[0];
      if (
        file &&
        file.url &&
        typeof file.url === "string" &&
        file.url.trim() !== "" &&
        file.url !== "["
      ) {
        const imageUrl = file.url;

        if (imageUrl.startsWith("/storage/") || imageUrl.startsWith("/")) {
          const baseUrl = process.env.VUE_APP_BASE_URL || "";
          return `${baseUrl}${imageUrl}`;
        }
        return imageUrl;
      }
    }

    // Handle images with better type checking
    if (listing.images) {
      let images = [];

      // Handle array case
      if (Array.isArray(listing.images)) {
        images = listing.images;
      }
      // Handle string case (single image)
      else if (typeof listing.images === "string") {
        images = [listing.images];
      }

      // Find first valid image
      for (const image of images) {
        if (
          image &&
          typeof image === "string" &&
          image.trim() !== "" &&
          image !== "["
        ) {
          if (image.startsWith("/storage/") || image.startsWith("/")) {
            const baseUrl = process.env.VUE_APP_BASE_URL || "";
            return `${baseUrl}${image}`;
          }

          if (image.startsWith("http://") || image.startsWith("https://")) {
            return image;
          }
        }
      }
    }
  } catch (error) {
    devLog("⚠️ Error processing image URL:", error, listing);
  }

  // Always return valid fallback
  return assetImages[Math.floor(Math.random() * assetImages.length)];
};

const handleImageError = (event) => {
  devLog("⚠️ Image failed to load:", event.target.src);

  // Set fallback image
  const fallbackImage =
    assetImages[Math.floor(Math.random() * assetImages.length)];
  event.target.src = fallbackImage;

  // Prevent infinite error loops
  event.target.onerror = null;

  devLog("✅ Fallback image set:", fallbackImage);
};

const handleImageLoad = () => {
  // Image loaded successfully - no action needed
};

// ===== NAVIGATION METHODS =====
const viewListingDetails = async (listing) => {
  if (process.env.NODE_ENV === "development") {
    console.log("🖱️ SlidingScrollable: viewListingDetails called", {
      id: listing.id,
      title: listing.title,
      sectionType: props.sectionType,
    });
  }

  console.log("See full listing object:", listing);

  try {
    // 1. Determine listing type and ensure proper data structure
    const listingType = detectListingType(listing);

    // 2. Normalize the listing data before storing
    const normalizedListing = normalizeListing(listing);

    // 3. Store the listing in appropriate store(s)
    if (listingType === "property") {
      await store.dispatch(
        "propertyFilter/setSelectedProperty",
        normalizedListing
      );
    }

    // Always store in general FilterStore as fallback
    await store.dispatch(
      "FilterStore/setSelectedListingItem",
      normalizedListing
    );

    // 4. Generate route configuration
    const slug = generateSlug(listing.title);
    const category = generateSlug(listing.category?.name || "general");
    const source =
      props.sectionType === "recent"
        ? `recently-viewed-${listingType}`
        : `saved-search-${listingType}`;

    // ✅ ADD: Handle job listings specifically
    if (isJobListing(listing)) {
      // Prefetch and store the job data by ID
      // const job = await store.dispatch(
      //   "jobs/fetchJobById",
      //   listing.job?.id || listing.id
      // );
      // store.dispatch("FilterStore/setSelectedListingItem", job);

      const routeConfig = {
        name: "ListingDetailsForJobs",
        params: {
          id: listing.job?.id || listing.id,
          slug: slug,
          listingId: listing.id || null,
        },
        query: {
          source: source,
          category: "jobs",
          from: props.sectionType || "home-categories",
        },
      };

      if (process.env.NODE_ENV === "development") {
        console.log("🚀 Navigating to job details:", routeConfig);
      }

      // Navigate to job details page
      await router.push(routeConfig);
    } else {
      // Handle non-job listings (existing logic)
      const routeConfig = {
        name: "ListingDetails",
        params: {
          id: listing.id,
          slug: slug,
        },
        query: {
          source: source,
          category: listingType,
          from: props.sectionType || "saved-searches",
        },
      };

      if (process.env.NODE_ENV === "development") {
        console.log("🚀 Navigating to:", routeConfig);
      }

      // Navigate to general details page
      await router.push(routeConfig);
    }

    // 6. Emit for parent component
    emit("viewDetails", listing);
  } catch (error) {
    console.error("❌ Failed to navigate to listing details:", error);
    // Fallback: Basic emit
    emit("viewDetails", listing);
  }
};

const generateSlug = (text) => {
  return text
    ? text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
    : "listing";
};

// const toggleFavorite = (listing) => {
//   emit('toggleFavorite', listing);
// };

// const isFavorite = (listingId) => {
//   return props.favorites.includes(listingId);
// };

// ===== LIFECYCLE HOOKS =====
const initializeScroll = async () => {
  await nextTick();

  if (slider.value) {
    updateScrollState();
  }
};

let resizeObserver = null;

onMounted(async () => {
  await initializeScroll();

  // Set up resize observer
  if (slider.value) {
    resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    resizeObserver.observe(slider.value);
  }
});

// Clean up on unmount
onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// Watch for changes in listings
watch(
  () => displayListings.value,
  async (newListings) => {
    if (newListings?.length > 0) {
      // Reset scroll position
      if (slider.value) {
        slider.value.scrollLeft = 0;
      }

      await nextTick();
      await initializeScroll();
    }
  },
  { immediate: true }
);

// Watch for loading state changes
watch(
  () => props.loading,
  async (isLoading, wasLoading) => {
    if (wasLoading && !isLoading && displayListings.value.length > 0) {
      devLog("⏳ Loading finished, reinitializing scroll");
      await nextTick();
      await initializeScroll();
    }
  }
);

// "View All" helpers (MOBILE ONLY)
const isViewAll = (item) => !!item.__viewAll;

// computed fallback link if parent didn't pass one
const resolvedSeeMore = computed(() => {
  if (props.seeMoreLink) return props.seeMoreLink;

  // sensible fallback (adjust to your app)
  const first = (displayListings.value || [])[0];
  if (!first) return null;

  return {
    name: "searchResults",
    query: {
      category: first.category_id || first.category?.id,
      categoryName: first.category?.name || "All",
    },
  };
});

const goSeeMore = () => {
  if (!resolvedSeeMore.value) return;
  router.push(resolvedSeeMore.value);
};

// Mobile grid data = 3 items + "View All" tile (if seeMoreLink exists)
// const mobileGridItems = computed(() => {
//   const all = displayListings.value || [];
//   const base = all.slice(0, 5);

//   // Add the tile if there are more to show OR a seeMoreLink was supplied
//   if (all.length > 5 || props.seeMoreLink) {
//     base.push({ id: '__view_all__', __viewAll: true });
//   }
//   return base;
// });

const mobileGridItems = computed(() => {
  const all = displayListings.value || [];
  const base = all.slice(0, 5);

  // show tile if there are more items than we show OR parent gave a link,
  // but only if we can actually navigate somewhere
  const hasMore = all.length > 5;
  if ((hasMore || props.seeMoreLink) && resolvedSeeMore.value) {
    base.push({ id: "__view_all__", __viewAll: true });
  }
  return base;
});

// Compact meta line per card (matches mock)
const getMobileMeta = (listing) => {
  if (isPropertyListing(listing)) {
    const b = listing.property_details?.bedrooms ?? listing.property?.bedrooms;
    const ba =
      listing.property_details?.bathrooms ?? listing.property?.bathrooms;
    if (b != null && ba != null) return `${b} Bed · ${ba} Baths`;
    if (b != null) return `${b} Bed`;
    if (ba != null) return `${ba} Baths`;
    return "—";
  }
  if (isCarListing(listing)) {
    const year = listing.car_details?.year;
    const km = listing.car_details?.kilometers;
    if (year && km) return `${year} · ${formatNumber(km)} km`;
    if (year) return `${year}`;
    if (km) return `${formatNumber(km)} km`;
    return listing.car_details?.bodyType || "—";
  }
  return isJobListing(listing)
    ? listing.company_name || listing.job?.company_name || "—"
    : listing.category?.name || "—";
};
</script>

<style scoped>
/* ===== MAIN CONTAINER ===== */
.sliding-scrollable-container {
  margin: 4px 0; /* ✅ CHANGED: Reduced from 40px to 4px */
  position: relative;
  width: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px; /* ✅ CHANGED: Reduced from 24px to 4px */
}

.section-title {
  /* Replace direct font-size with font system class */
  font-size: var(--font-size-xl) !important; /* Reduced from 24px to 20px */
  font-weight: var(--font-weight-semibold) !important;
  margin: 0;
  color: var(--text-primary);
}

.section-title.recent-title {
  font-size: var(--font-size-2xl) !important; /* Reduced from 28px to 24px */
  font-weight: var(--font-weight-bold) !important;
}

.see-more-link {
  color: #e50000;
  text-decoration: none;
  font-size: var(--font-size-sm) !important; /* Reduced from 14px to 14px */
  font-weight: var(--font-weight-medium) !important;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.see-more-link:hover {
  transform: translateX(4px);
}

/* ===== LISTING SLIDER ===== */
.listing-slider-container {
  position: relative;
  width: 100%;
}

.listing-slider {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 16px 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;
  gap: 16px;
  scroll-snap-type: x mandatory;
}

.listing-slider::-webkit-scrollbar {
  display: none;
}

/* ===== NAVIGATION BUTTONS ===== */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 10;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.nav-button:hover:not(:disabled) {
  opacity: 1;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: translateY(-50%) scale(0.9);
}

.prev-button {
  left: -8px;
}

.next-button {
  right: -8px;
}

/* ===== LISTING CARDS ===== */
.listing-card {
  flex: 0 0 auto;
  /* Responsive card widths */
  width: calc((100% - 64px) / 5); /* 5 cards on desktop */
  min-width: 220px;
  max-width: 280px;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  /* NO DEFAULT SHADOW - removed border and box-shadow */
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  position: relative;
  scroll-snap-align: start;
  transform: translateY(0);
  will-change: transform, box-shadow;
}

/* HOVER EFFECTS ONLY */
.listing-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.listing-image-container {
  height: 180px;
  position: relative;
  overflow: hidden;
}

.listing-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.listing-card:hover .listing-image {
  transform: scale(1.08);
}

.listing-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #e50000;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

/* ✅ UPDATED: Sleek Verified Badge */
.verified-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 3px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.verified-badge span {
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ✅ ALTERNATIVE: Side-by-side layout for both badges */
.listing-image-container:has(.featured-badge) .verified-badge {
  left: auto;
  right: 50px; /* Position next to favorite button */
  top: 8px;
}

.favorite-button:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* ✅ UPDATED: Favorite button styles to match v-btn */
.favorite-button {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  padding: 0; /* ✅ ADDED: Remove any default padding */
}

.favorite-button .v-icon {
  margin: 0 !important; /* ✅ FIXED: Remove all margins */
  padding: 0 !important; /* ✅ FIXED: Remove all padding */
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important; /* ✅ ADDED: Reset line height */
}

.favorite-button.favorited {
  background: rgba(255, 240, 240, 0.95);
  box-shadow: 0 2px 8px rgba(229, 0, 0, 0.3);
}

.favorite-button.favorited:hover {
  background: rgba(255, 240, 240, 1);
  box-shadow: 0 4px 16px rgba(229, 0, 0, 0.4);
}

/* ✅ UPDATED: Sleek Featured Badge */
.featured-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 3px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.featured-badge span {
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== LISTING CONTENT ===== */
.listing-content {
  padding: 16px;
}

/* ✅ ENHANCED: Bold red price */
.listing-price {
  font-size: var(--font-size-base) !important; /* Reduced from 18px to 16px */
  font-weight: var(--font-weight-bold) !important;
  /*color: #e50000;*/
  color: #000080;
  margin-bottom: 8px;
}

/* ✅ MODIFIED: Title with single-line ellipsis */
.listing-title {
  font-size: var(--font-size-sm) !important;
  font-weight: var(--font-weight-semibold) !important;
  margin: 0 0 8px 0;
  line-height: 1.4;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Removed multi-line clamp properties */
}

/* ✅ ENHANCED: Location styling */
.listing-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs) !important; /* Reduced from 13px to 12px */
  color: var(--text-secondary);
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ MODIFIED: Specs container limited to single row with flex */
.listing-specs {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 100%;
}

.car-specs,
.property-specs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ✅ MODIFIED: Spec items with proper truncation */
.spec-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs) !important;
  color: var(--text-secondary);
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 12px;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.spec-item .v-icon {
  font-size: 14px !important;
}

/* ✅ ENHANCED: Category tags */
.listing-category {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.category-tag {
  background-color: #e3f2fd;
  color: #1976d2;
  font-size: var(--font-size-xs) !important; /* Kept at 11px */
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: var(--font-weight-medium) !important;
}

.subcategory-tag {
  background-color: #f3e5f5;
  color: #7b1fa2;
  font-size: var(--font-size-xs) !important; /* Kept at 11px */
  padding: 2px 6px;
  border-radius: 8px;
}

/* ✅ MODIFIED: Category tags with truncation */
.category-tag,
.subcategory-tag {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

/* ✅ MAINTAINED: Legacy property tags */
.listing-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.listing-tag {
  background-color: #f0f0f0;
  color: #666;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

/* ===== SCROLL INDICATORS ===== */
.scroll-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 3px; /* ✅ CHANGED: Reduced from 20px to 3px */
}

.scroll-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ddd;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scroll-indicator.active {
  background-color: #e50000;
  transform: scale(1.3);
}

.scroll-indicator:hover {
  background-color: #e50000;
  opacity: 0.7;
}

/* ===== LOADING, ERROR, EMPTY STATES ===== */
.loading-container {
  padding: 20px 0;
}

.listing-cards-skeleton {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 16px 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.listing-cards-skeleton::-webkit-scrollbar {
  display: none;
}

.listing-card-skeleton {
  flex: 0 0 auto;
  width: calc((100% - 64px) / 5);
  min-width: 220px;
  max-width: 280px;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.skeleton-image {
  height: 180px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 16px;
}

.skeleton-title {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 12px;
  border-radius: 4px;
}

.skeleton-price {
  height: 24px;
  width: 70%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 16px;
  border-radius: 4px;
}

.skeleton-details {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.empty-container,
.error-container {
  padding: 32px 20px; /* ✅ CHANGED: Reduced from 60px to 32px */
  text-align: center;
  background-color: #f9f9f9;
  border-radius: 12px;
  border: 1px dashed #ddd;
  color: #666;
}

.empty-container p,
.error-container p {
  margin: 12px 0;
  font-size: 16px;
}

.error-container {
  color: #e65c5c;
}
/* ✅ NEW: Improved spacing for property listings with reordered elements */
.property-first {
  margin-bottom: 8px;
}

/* ✅ ENHANCED: Smooth animations */
@keyframes heartBeat {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.favorite-button.favorited .v-icon {
  animation: heartBeat 0.6s ease-in-out;
}

/* ✅ ENHANCED: Badge hover effects */
.featured-badge:hover,
.verified-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}
/* ===== RESPONSIVE BREAKPOINTS ===== */

/* Small Mobile (< 480px) - Additional text truncation adjustments */
@media (max-width: 479px) {
  /* ...existing styles... */

  /* ✅ MODIFIED: Tighter constraints for small screens */
  .spec-item {
    max-width: 70px;
    padding: 1px 3px;
  }

  .category-tag,
  .subcategory-tag {
    max-width: 90px;
  }

  /* Force one line for all text elements */
  .listing-title,
  .listing-location span,
  .spec-item span,
  .listing-price,
  .listing-category span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
/* Responsive adjustments */
@media (max-width: 768px) {
  .section-title {
    font-size: var(--font-size-lg) !important; /* Reduced from 20px to 18px */
  }

  .see-more-link {
    font-size: var(--font-size-xs) !important; /* Reduced from 13px to 12px */
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: var(--font-size-base) !important; /* Reduced from 18px to 16px */
  }

  .listing-price {
    font-size: var(--font-size-sm) !important; /* Reduced from 16px to 14px */
  }

  .listing-title {
    font-size: var(--font-size-xs) !important; /* Reduced from 15px to 12px */
  }

  .spec-item {
    font-size: 10px !important; /* Already small, keeping at 10px */
  }
}
/* Large Desktop (1920px+) - 5 cards with more padding */
@media (min-width: 1920px) {
  .sliding-scrollable-container {
    padding: 0 40px;
  }

  .listing-card {
    width: calc((100% - 80px) / 5);
    min-width: 250px;
    max-width: 320px;
  }

  .listing-card-skeleton {
    width: calc((100% - 80px) / 5);
    min-width: 250px;
    max-width: 320px;
  }
}

/* Standard Desktop (1200px - 1919px) - 5 cards */
@media (min-width: 1200px) and (max-width: 1919px) {
  .listing-card {
    width: calc((100% - 64px) / 5);
    min-width: 220px;
    max-width: 280px;
  }

  .listing-card-skeleton {
    width: calc((100% - 64px) / 5);
    min-width: 220px;
    max-width: 280px;
  }
}

/* Laptop (992px - 1199px) - 5 cards, slightly smaller */
@media (min-width: 992px) and (max-width: 1199px) {
  .listing-card {
    width: calc((100% - 64px) / 5);
    min-width: 200px;
    max-width: 240px;
  }

  .listing-card-skeleton {
    width: calc((100% - 64px) / 5);
    min-width: 200px;
    max-width: 240px;
  }

  .listing-image-container {
    height: 160px;
  }
}

/* Tablet (768px - 991px) - 3 cards */
@media (min-width: 768px) and (max-width: 991px) {
  .listing-card {
    width: calc((100% - 32px) / 3);
    min-width: 220px;
    max-width: 280px;
  }

  .listing-card-skeleton {
    width: calc((100% - 32px) / 3);
    min-width: 220px;
    max-width: 280px;
  }

  .listing-image-container {
    height: 160px;
  }

  .section-title {
    font-size: 20px;
  }

  .see-more-link {
    font-size: 13px;
  }

  .nav-button {
    width: 36px;
    height: 36px;
  }
}

/* Mobile (480px - 767px) - 2 cards */
@media (min-width: 480px) and (max-width: 767px) {
  .listing-card {
    width: calc((100% - 16px) / 2);
    min-width: 180px;
    max-width: 220px;
  }

  .listing-card-skeleton {
    width: calc((100% - 16px) / 2);
    min-width: 180px;
    max-width: 220px;
  }

  .listing-image-container {
    height: 140px;
  }

  .listing-price {
    font-size: 16px;
  }

  .listing-title {
    font-size: 15px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .nav-button {
    width: 32px;
    height: 32px;
  }
}

/* Small Mobile (< 480px) - 2 cards, more compact */
@media (max-width: 479px) {
  .listing-card {
    width: calc((100% - 16px) / 2);
    min-width: 160px;
    max-width: 200px;
  }

  .listing-card-skeleton {
    width: calc((100% - 16px) / 2);
    min-width: 160px;
    max-width: 200px;
  }

  .listing-image-container {
    height: 120px;
  }

  .listing-content {
    padding: 12px;
  }

  .listing-price {
    font-size: 14px;
  }

  .listing-title {
    font-size: 13px;
  }

  .section-title {
    font-size: 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
  }

  .nav-button {
    width: 28px;
    height: 28px;
  }

  .sliding-scrollable-container {
    margin: 20px 0;
  }

  /* ✅ RESPONSIVE: Adjust specs for mobile */
  .spec-item {
    font-size: 10px;
    padding: 1px 4px;
  }

  .spec-item .v-icon {
    font-size: 12px !important;
  }

  .listing-specs {
    gap: 4px;
    margin-bottom: 6px;
  }
}

/* ✅ RESPONSIVE: Smaller badges on mobile */
@media (max-width: 768px) {
  .featured-badge,
  .verified-badge {
    font-size: 9px;
    padding: 3px 6px;
    border-radius: 10px;
  }

  .featured-badge .v-icon,
  .verified-badge .v-icon {
    font-size: 10px !important;
  }

  .favorite-button {
    width: 28px;
    height: 28px;
    top: 6px;
    right: 6px;
    padding: 0;
  }

  .favorite-button .v-icon {
    font-size: 16px !important;
    margin: 0 !important;
  }
}

@media (max-width: 480px) {
  .featured-badge,
  .verified-badge {
    font-size: 8px;
    padding: 2px 5px;
    border-radius: 8px;
  }

  .featured-badge .v-icon,
  .verified-badge .v-icon {
    font-size: 9px !important;
  }

  .favorite-button {
    width: 24px;
    height: 24px;
    top: 5px;
    right: 5px;
    padding: 0;
  }

  .favorite-button .v-icon {
    font-size: 14px !important;
    margin: 0 !important;
  }
}

/* ✅ ADD: Job listing specific styles */
.job-listing-card {
  border-left: 4px solid #3949ab !important;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.job-content {
  padding: 16px;
}

.job-title {
  font-size: var(--font-size-base) !important;
  font-weight: var(--font-weight-bold) !important;
  margin-bottom: 8px !important;
  color: #333 !important;
  line-height: 1.3 !important;
}

.company-name {
  font-size: var(--font-size-sm) !important;
  color: #555;
  font-weight: var(--font-weight-medium) !important;
  margin-bottom: 12px;
  letter-spacing: 0.01em;
}

.job-badges {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.feature-badge {
  background-color: #ffb300;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.verified-badge {
  background-color: #2196f3;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.job-details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 12px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 12px 0;
}

.job-detail-item {
  display: flex;
  flex-direction: column;
}

.job-detail-label {
  font-size: 0.65rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.job-detail-value {
  font-size: 0.85rem;
  color: #333;
  font-weight: 500;
}

.title-category-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 2px;
}

.category-name {
  font-size: 0.78rem;
  color: #888;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 2px;
  letter-spacing: 0.01em;
}

/* ✅ RESPONSIVE: Job details for mobile */
@media (max-width: 600px) {
  .job-details-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .job-detail-label {
    font-size: 0.6rem;
  }

  .job-detail-value {
    font-size: 0.8rem;
  }

  .job-title {
    font-size: var(--font-size-sm) !important;
  }

  .company-name {
    font-size: var(--font-size-xs) !important;
  }
}

/* ===== Mobile section header ===== */
.mobile-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px 8px;
  margin-top: 4px;
}

.mobile-section-title {
  font-weight: 700;
  font-size: 14px;
  color: #101828;
}

.mobile-circle-next {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.12);
}

/* ===== Mobile grid ===== */
.mobile-grid-wrap {
  padding: 0 8px 6px;
}
/*
.mobile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 10px;
  flex-wrap: nowrap
}
  */

.mobile-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.06);
}

.mobile-img {
  width: 100%;
  aspect-ratio: 4 / 3; /* similar to screenshot card aspect */
  overflow: hidden;
  border-bottom: 1px solid #f0f2f5;
}
.mobile-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-price {
  color: #e50000;
  font-weight: 800;
  font-size: 13px;
  padding: 8px 10px 2px;
}

.mobile-meta {
  color: #475467;
  font-size: 11px;
  padding: 0 10px 2px;
}

.mobile-loc {
  color: #667085;
  font-size: 11px;
  padding: 0 10px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Hide desktop nav buttons and dots on mobile */
@media (max-width: 959px) {
  .nav-button {
    display: none !important;
  }
  .scroll-indicators {
    display: none !important;
  }
}

/* Slightly smaller on very small phones */
@media (max-width: 360px) {
  .mobile-section-title {
    font-size: 13px;
  }
  .mobile-price {
    font-size: 12px;
  }
  .mobile-meta,
  .mobile-loc {
    font-size: 10px;
  }
}

/* ===== Mobile grid wrapper ===== */
.mobile-grid-wrap {
  padding: 0 8px;
}

/*
.mobile-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
  */

/* ===== Mobile normal card ===== */
.mobile-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 160px;
}

.mobile-card img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  margin-bottom: 6px;
}

.mobile-price {
  font-weight: 700;
  font-size: 14px;
  color: #000080;
  margin-bottom: 2px;
}

.mobile-meta {
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
}

.mobile-loc {
  font-size: 11px;
  color: #999;
}

/* ===== View All special tile ===== */
.mobile-viewall {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-viewall-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  

}

.router-link{
  text-decoration: none !important;
}

.mobile-viewall-circle {
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
}

.mobile-viewall-circle v-icon,
.mobile-viewall-circle .v-icon {
  color: #333;
}

.mobile-viewall-text {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

/* ===== MOBILE: make cards a single horizontal row with hidden scrollbar ===== */
@media (max-width: 959px) {
  /* turn the grid into a horizontal scroller */
  .mobile-grid {
    display: flex !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    gap: 12px !important;
    padding: 0 8px 6px !important;
    -webkit-overflow-scrolling: touch; /* smooth iOS scroll */
    scroll-snap-type: x mandatory; /* nice snap effect */
    scrollbar-width: none; /* Firefox: hide bar */
  }
  .mobile-grid::-webkit-scrollbar {
    display: none; /* WebKit: hide bar */
  }

  /* each card takes a fixed width and snaps */
  .mobile-card {
    flex: 0 0 72% !important; /* ~3/4 width; tweak as you like */
    max-width: 320px !important;
    min-width: 240px !important;
    scroll-snap-align: start;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); /* keep your card look */
  }

  /* image sizing inside the card stays responsive */
  .mobile-card img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
  }

  /* optional: tighten title/meta for narrow widths */
  .mobile-price {
    font-size: 14px;
  }
  .mobile-meta,
  .mobile-loc {
    font-size: 12px;
  }
}

/* keep desktop nav buttons/indicators hidden on mobile, as you already had */
@media (max-width: 959px) {
  .nav-button,
  .scroll-indicators {
    display: none !important;
  }
}
</style>
